import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  IconButton,
  InputAdornment
} from '@mui/material';
import Select, { createFilter } from 'react-select';
import { Close as CloseIcon, Warning as WarningIcon } from '@mui/icons-material';
import { FormButton } from '../../components/ui/FormComponents';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import Toggle from '../../components/ui/Toggle';
import { caisseAPI } from '../../services/api';
import { getRoleLabel, getRoleOptions } from '../../utils/roleUtils';
import { CustomDropdownIndicator, MultiselectValueContainer, MultiselectOption } from '../../components/ui/MultiselectComponents';
import { useMultiselectMenuControl } from '../../hooks/useMultiselectMenuControl';

const UserEditModal = ({ open, onClose, onSubmit, loading, userData }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    role: null,
    caisseIds: [],
    active: true
  });
  const [errors, setErrors] = useState({});
  const [caisses, setCaisses] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogType, setConfirmDialogType] = useState('generic');
  const [userName, setUserName] = useState('');
  const firstInputRef = useRef(null);
  
  // Use shared hook for multiselect menu control
  const { menuIsOpen, setMenuIsOpen, selectWrapperRef, lastActionRef } = useMultiselectMenuControl(true);

  // Load caisses for caisse dropdown
  const loadCaisses = async () => {
    try {
      const response = await caisseAPI.getAll();
      const caisses = response.data.content || [];
      setCaisses(caisses.filter(caisse => caisse.active));
    } catch (error) {
      console.error('Error loading caisses:', error);
    }
  };

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open && userData) {
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        username: userData.username || '',
        role: userData.role ? { value: userData.role, label: getRoleLabel(userData.role) } : null,
        caisseIds: userData.assignedCaisses && userData.assignedCaisses.length > 0 
          ? (userData.role === 'AGENT' 
              ? { value: userData.assignedCaisses[0].id, label: userData.assignedCaisses[0].name }
              : userData.assignedCaisses.map(caisse => ({ value: caisse.id, label: caisse.name })))
          : (userData.role === 'AGENT' ? null : []),
        active: userData.active !== undefined ? userData.active : true
      });
      setErrors({});
      setShowPasswordField(false);
      
      // Auto-focus the first input field
      setTimeout(() => {
        if (firstInputRef.current) {
          firstInputRef.current.focus();
        }
      }, 100); // Small delay to ensure modal is fully rendered
    } else if (!open) {
      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        role: null,
        caisseIds: [],
        active: true
      });
      setErrors({});
      setShowPasswordField(false);
    }
  }, [open, userData]);

  useEffect(() => {
    if (open) {
      loadCaisses();
    }
  }, [open]);


  const handleInputChange = (fieldName, value) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [fieldName]: value
      };
      
      // If role changes, clear caisse selection appropriately
      if (fieldName === 'role') {
        const newRole = value?.value || value;
        if (newRole === 'AGENT') {
          newData.caisseIds = null; // Single select
        } else if (newRole === 'VERIFICATION') {
          newData.caisseIds = []; // Multi select
        } else {
          newData.caisseIds = null; // No caisse assignment for MANAGER
        }
      }
      
      return newData;
    });
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Le prénom est requis';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Le nom est requis';
    }
    if (!formData.username.trim()) {
      newErrors.username = 'Le nom d\'utilisateur est requis';
    }
    if (!formData.role) {
      newErrors.role = 'Le rôle est requis';
    }
    
    // Caisse is required for AGENT and VERIFICATION roles
    const selectedRole = formData.role?.value || formData.role;
    if (selectedRole === 'AGENT' && !formData.caisseIds) {
      newErrors.caisseIds = 'La caisse est requise pour un agent';
    } else if (selectedRole === 'VERIFICATION' && (!formData.caisseIds || formData.caisseIds.length === 0)) {
      newErrors.caisseIds = 'Au moins une caisse est requise pour un agent de vérification';
    }
    
    if (showPasswordField && !formData.password?.trim()) {
      newErrors.password = 'Le nouveau mot de passe est requis';
    } else if (showPasswordField && formData.password && formData.password.length < 6) {
      newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const hasChanges = () => {
    if (!userData) return false;
    
    // Check if any field has changed
    const originalData = {
      firstName: userData.firstName || '',
      lastName: userData.lastName || '',
      username: userData.username || '',
      role: userData.role || null,
      caisseIds: userData.assignedCaisses ? userData.assignedCaisses.map(c => c.id) : [],
      active: userData.active !== undefined ? userData.active : true
    };

    // Compare current form data with original data
    const currentRole = formData.role && typeof formData.role === 'object' ? formData.role.value : formData.role;
    const hasBasicChanges = 
      formData.firstName !== originalData.firstName ||
      formData.lastName !== originalData.lastName ||
      formData.username !== originalData.username ||
      currentRole !== originalData.role ||
      formData.active !== originalData.active;

    // Compare caisseIds arrays - extract IDs from react-select objects
    const currentCaisseIds = Array.isArray(formData.caisseIds) 
      ? formData.caisseIds.map(item => item.value || item).sort() 
      : (formData.caisseIds && formData.caisseIds.value ? [formData.caisseIds.value] : []);
    const originalCaisseIds = Array.isArray(originalData.caisseIds) ? originalData.caisseIds.sort() : [];
    const hasCaisseChanges = JSON.stringify(currentCaisseIds) !== JSON.stringify(originalCaisseIds);

    // Check if password field is shown and has a value
    const hasPasswordChange = showPasswordField && formData.password && formData.password.trim() !== '';

    return hasBasicChanges || hasCaisseChanges || hasPasswordChange;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (isValid) {
      if (hasChanges()) {
        // Check if only the active status changed
        const originalActive = userData.active !== undefined ? userData.active : true;
        const currentActive = formData.active;
        
        // Check if only active status changed (no other changes)
        const hasOtherChanges = () => {
          const originalData = {
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            username: userData.username || '',
            role: userData.role || null,
            caisseIds: userData.assignedCaisses ? userData.assignedCaisses.map(c => c.id) : []
          };

          const currentRole = formData.role && typeof formData.role === 'object' ? formData.role.value : formData.role;
          const hasBasicChanges = 
            formData.firstName !== originalData.firstName ||
            formData.lastName !== originalData.lastName ||
            formData.username !== originalData.username ||
            currentRole !== originalData.role;

          // Compare caisseIds arrays
          const currentCaisseIds = Array.isArray(formData.caisseIds) 
            ? formData.caisseIds.map(item => item.value || item).sort() 
            : (formData.caisseIds && formData.caisseIds.value ? [formData.caisseIds.value] : []);
          const originalCaisseIds = Array.isArray(originalData.caisseIds) ? originalData.caisseIds.sort() : [];
          const hasCaisseChanges = JSON.stringify(currentCaisseIds) !== JSON.stringify(originalCaisseIds);

          // Check if password field is shown and has a value
          const hasPasswordChange = showPasswordField && formData.password && formData.password.trim() !== '';

          return hasBasicChanges || hasCaisseChanges || hasPasswordChange;
        };

        if (!hasOtherChanges() && originalActive !== currentActive) {
          // Only active status changed
          setConfirmDialogType(currentActive ? 'activate' : 'deactivate');
          setUserName(`${userData.firstName} ${userData.lastName}`);
        } else {
          // Other changes (with or without active status change)
          setConfirmDialogType('generic');
        }
        
        setShowConfirmDialog(true);
      } else {
        // No changes, just close the modal
        onClose();
      }
    }
  };

  const handleConfirmEdit = () => {
    setShowConfirmDialog(false);
    
    const dataToSubmit = { ...formData };
    if (showPasswordField && (!dataToSubmit.password || dataToSubmit.password.trim() === '')) {
      delete dataToSubmit.password;
    }
    
    onSubmit(dataToSubmit);
  };

  const handleResetPassword = () => {
    setShowPasswordField(true);
    setFormData(prev => ({ ...prev, password: '' }));
  };


  // Helper to check if multiselect has value
  const hasMultiselectValue = (value) => {
    return Array.isArray(value) && value.length > 0;
  };

  // Custom styles for react-select with gray chevron
  const getCustomSelectStyles = (fieldName, hasError = false) => ({
    control: (provided, state) => ({
      ...provided,
      backgroundColor: '#ffffff',
      border: hasError ? '2px solid #EF456E' : (state.isFocused || (formData[fieldName] && formData[fieldName].toString().trim() !== '') ? '2px solid #007BC6' : '1px solid #E3E3E3'),
      borderRadius: '8px',
      height: '48px',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      paddingLeft: '12px',
      paddingRight: '12px',
      '&:hover': {
        border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      height: '48px',
      minHeight: '48px',
      overflow: 'hidden',
      flex: 1,
      justifyContent: 'flex-start',
      flexWrap: 'nowrap',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      color: '#000000',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
    }),
    placeholder: (provided) => ({
      ...provided,
      marginLeft: '0',
      marginRight: '0',
      paddingLeft: '0',
      paddingRight: '0',
      color: '#A3A3A3',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      textTransform: 'none',
    }),
    singleValue: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
      color: '#000000',
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      textTransform: 'none',
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#F3F4F6',
      borderRadius: '6px',
      margin: '2px',
      maxWidth: '120px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#007BC6',
      fontSize: '14px',
      fontWeight: 600,
      fontFamily: 'Montserrat, sans-serif',
      padding: '2px 6px',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#6B7280',
      '&:hover': {
        backgroundColor: '#E5E7EB',
        color: '#374151',
      },
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      color: '#6B7280',
      padding: '8px',
      '&:hover': {
        color: '#374151',
      },
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#6B7280',
      padding: '8px',
      '&:hover': {
        color: '#374151',
      },
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 999999,
      boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)',
      borderRadius: '8px',
      border: '1px solid #E3E3E3',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 999999,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#F0F8FF' : state.isFocused ? '#F3F4F6' : '#ffffff',
      color: state.isSelected ? '#027BC6' : '#000000',
      fontSize: '16px',
      fontWeight: state.isSelected ? 700 : 600,
      fontFamily: 'Montserrat, sans-serif',
      padding: '12px 16px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected ? '#E6F3FF' : '#F3F4F6',
      },
    }),
  });

  const roleOptions = getRoleOptions();

  const caisseOptions = caisses.map(caisse => ({
    value: caisse.id,
    label: caisse.name
  }));

  const renderField = (field, index) => {
    const hasError = !!errors[field.name];
    
    if (field.type === 'select') {
      return (
        <Box>
          <Select
            options={field.options}
            value={formData[field.name]}
            onChange={(selectedOption) => handleInputChange(field.name, selectedOption)}
            placeholder={field.placeholder}
            isSearchable={false}
            styles={getCustomSelectStyles(field.name, hasError)}
            components={{
              DropdownIndicator: (props) => <CustomDropdownIndicator {...props} />,
            }}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {hasError && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginLeft: '14px' }}>
              <WarningIcon sx={{ color: '#EF456E', fontSize: '16px', marginRight: '4px' }} />
              <Typography
                sx={{
                  color: '#EF456E',
                  fontSize: '12px'
                }}
              >
                {errors[field.name]}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    if (field.type === 'multiselect') {
      const hasValue = hasMultiselectValue(formData[field.name]);
      
      return (
        <Box>
          <Box ref={selectWrapperRef}>
            <Select
              options={field.options}
              value={formData[field.name]}
              onChange={(selectedOptions) => handleInputChange(field.name, selectedOptions)}
              placeholder={field.placeholder}
              isMulti={true}
              isSearchable={false}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              styles={getCustomSelectStyles(field.name, hasError)}
              filterOption={createFilter({
                ignoreCase: true,
                trim: true,
                matchFrom: 'any',
              })}
              components={{
                Option: (props) => <MultiselectOption {...props} />,
                MultiValue: () => null, // Hide individual tags
                ValueContainer: (props) => (
                  <MultiselectValueContainer 
                    {...props} 
                    getValue={props.getValue}
                    countColor="#000000"
                  />
                ),
                ClearIndicator: () => null, // Hide default clear indicator
                DropdownIndicator: (props) => (
                  <CustomDropdownIndicator 
                    {...props} 
                    hasValue={hasValue}
                    onClear={() => handleInputChange(field.name, [])}
                    isMulti={true}
                  />
                ),
              }}
              menuIsOpen={menuIsOpen}
              onMenuOpen={() => {
                setMenuIsOpen(true);
                lastActionRef.current = 'menu-open';
              }}
              onMenuClose={() => {
                setMenuIsOpen(false);
                lastActionRef.current = 'menu-close';
              }}
              onInputChange={(inputValue, { action }) => {
                lastActionRef.current = action;
              }}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </Box>
          {hasError && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginLeft: '14px' }}>
              <WarningIcon sx={{ color: '#EF456E', fontSize: '16px', marginRight: '4px' }} />
              <Typography
                sx={{
                  color: '#EF456E',
                  fontSize: '12px'
                }}
              >
                {errors[field.name]}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    if (field.type === 'password') {
      return (
        <Box>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            value={formData[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            error={!!errors[field.name]}
            helperText=""
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ mr: 1 }}>
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent'
                      }
                    }}
                  >
                    <svg 
                      width="20" 
                      height="20" 
                      fill="none" 
                      stroke="#000000" 
                      viewBox="0 0 24 24"
                    >
                      {showPassword ? (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18" />
                        </>
                      ) : (
                        <>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '48px',
                paddingLeft: '12px',
                paddingRight: '12px',
                borderRadius: '8px',
                backgroundColor: '#ffffff',
                border: hasError ? '2px solid #EF456E' : '1px solid #E3E3E3',
                '&:hover': {
                  border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
                },
                '&.Mui-focused': {
                  border: hasError ? '2px solid #EF456E' : '2px solid #007BC6',
                  boxShadow: 'none',
                },
                '& fieldset': {
                  border: 'none',
                },
              },
              '& .MuiInputBase-input': {
                padding: '0 !important',
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif',
                color: '#000000',
                '&::placeholder': {
                  color: '#A3A3A3',
                  opacity: 1,
                  fontSize: '16px',
                  fontWeight: 600,
                  fontFamily: 'Montserrat, sans-serif',
                },
              },
              '& .MuiFormHelperText-root': {
                marginLeft: '14px',
                fontSize: '12px',
                fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif',
              },
            }}
          />
          {hasError && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginLeft: '14px' }}>
              <WarningIcon sx={{ color: '#EF456E', fontSize: '16px', marginRight: '4px' }} />
              <Typography
                sx={{
                  color: '#EF456E',
                  fontSize: '12px'
                }}
              >
                {errors[field.name]}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }


    // Regular text input
    return (
      <Box>
        <TextField
          fullWidth
          value={formData[field.name] || ''}
          onChange={(e) => handleInputChange(field.name, e.target.value)}
          placeholder={field.placeholder}
          error={!!errors[field.name]}
          helperText=""
          inputRef={index === 0 ? firstInputRef : null} // Add ref to first field
          sx={{
            '& .MuiOutlinedInput-root': {
              height: '48px',
              paddingLeft: '12px',
              paddingRight: '12px',
              borderRadius: '8px',
              backgroundColor: '#ffffff',
              border: hasError ? '2px solid #EF456E' : '1px solid #E3E3E3',
              '&:hover': {
                border: hasError ? '2px solid #EF456E' : '1px solid #007BC6',
              },
              '&.Mui-focused': {
                border: hasError ? '2px solid #EF456E' : '2px solid #007BC6',
                boxShadow: 'none',
              },
              '& fieldset': {
                border: 'none',
              },
            },
            '& .MuiInputBase-input': {
              padding: '0 !important',
              fontSize: '16px',
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif',
              color: '#000000',
              '&::placeholder': {
                color: '#A3A3A3',
                opacity: 1,
                fontSize: '16px',
                fontWeight: 600,
                fontFamily: 'Montserrat, sans-serif',
              },
            },
            '& .MuiFormHelperText-root': {
              marginLeft: '14px',
              fontSize: '12px',
              fontWeight: 600,
              fontFamily: 'Montserrat, sans-serif',
            },
          }}
        />
        {hasError && (
          <Box sx={{ display: 'flex', alignItems: 'center', marginTop: '4px', marginLeft: '14px' }}>
            <WarningIcon sx={{ color: '#EF456E', fontSize: '16px', marginRight: '4px' }} />
            <Typography
              sx={{
                color: '#EF456E',
                fontSize: '12px'
              }}
            >
              {errors[field.name]}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  // Build fields array based on current form state
  const getFields = useCallback(() => {
    const baseFields = [
      { name: 'firstName', type: 'text', placeholder: 'Prénom', required: true },
      { name: 'lastName', type: 'text', placeholder: 'Nom', required: true },
      { name: 'username', type: 'text', placeholder: 'Nom d\'utilisateur', required: true },
      { name: 'role', type: 'select', placeholder: 'Rôle', required: true, options: roleOptions }
    ];

    // Add password field if reset password was clicked
    if (showPasswordField) {
      baseFields.splice(3, 0, { name: 'password', type: 'password', placeholder: 'Nouveau mot de passe', required: true });
    }

    // Add caisse field for AGENT (single select) and VERIFICATION (multiselect) roles
    const selectedRole = formData.role?.value || formData.role;
    if (selectedRole === 'AGENT') {
      baseFields.push({ name: 'caisseIds', type: 'select', placeholder: 'Caisse', required: true, options: caisseOptions });
    } else if (selectedRole === 'VERIFICATION') {
      baseFields.push({ name: 'caisseIds', type: 'multiselect', placeholder: 'Caisses', required: true, options: caisseOptions });
    }

    return baseFields;
  }, [formData.role, showPasswordField, roleOptions, caisseOptions]);

  const fields = useMemo(() => getFields(), [getFields]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          boxShadow: '0px 20px 80px #00000011',
          backgroundColor: '#F9FAFC',
          overflow: 'visible',
          // Align with CreationModal sizing strategy for comparison
          width: '800px',
          maxWidth: 'none',
          margin: 0
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          overflow: 'visible'
        },
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '20vh'
        }
      }}
    >
      <DialogContent
        sx={{
          padding: 0,
          overflow: 'visible'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px 0 32px',
            marginBottom: '24px'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontWeight: 600,
              fontSize: '20px',
              lineHeight: '24px',
              color: '#1A1A1A',
              textTransform: 'none',
            }}
          >
            Modifier l'utilisateur
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Toggle
              checked={formData.active}
              onChange={() => setFormData(prev => ({ ...prev, active: !prev.active }))}
              label={formData.active ? 'Actif' : 'Inactif'}
            />
            <IconButton
              onClick={onClose}
              sx={{
                color: '#6B7280',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          {/* Form Fields Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '16px',
              padding: '0 32px',
              marginBottom: '16px'
            }}
          >
            {fields.map((field, index) => (
              <Box key={field.name} sx={{ minWidth: 0, overflow: 'hidden', minHeight: '88px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '16px',
                    color: '#6B7280',
                    textTransform: 'none',
                    marginBottom: '8px',
                    letterSpacing: '0.5px'
                  }}
                >
                  {field.name === 'firstName' && 'PRÉNOM'}
                  {field.name === 'lastName' && 'NOM'}
                  {field.name === 'username' && 'NOM D\'UTILISATEUR'}
                  {field.name === 'password' && 'NOUVEAU MOT DE PASSE'}
                  {field.name === 'role' && 'RÔLE'}
                  {field.name === 'caisseIds' && 'CAISSE'}
                </Typography>
                {renderField(field, index)}
              </Box>
            ))}
          </Box>

          {/* Action Buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '0 32px 32px 32px',
            }}
          >
            {/* Reset Password Link */}
            <Typography
              onClick={handleResetPassword}
              sx={{
                fontFamily: 'Montserrat',
                fontWeight: 'bold',
                fontSize: '14px',
                lineHeight: '32px',
                color: '#007BC6',
                cursor: 'pointer',
                textDecoration: 'underline',
                textTransform: 'none',
                letterSpacing: '0px',
                opacity: 1,
                '&:hover': {
                  color: '#005a9c',
                },
              }}
            >
              Réinitialiser le mot de passe
            </Typography>

            {/* Action Buttons */}
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              alignItems: 'center',
              mt: 2
            }}>
              <FormButton
                type="button"
                variant="text"
                onClick={onClose}
                disabled={loading}
                sx={{
                  color: '#36495C',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  minWidth: 'auto',
                  px: 2,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                  }
                }}
              >
                Annuler
              </FormButton>
              
              <FormButton
                type="submit"
                variant="contained"
                disabled={loading}
                loading={loading}
                sx={{
                  width: '120px',
                  height: '48px',
                  backgroundColor: '#027BC6',
                  boxShadow: '0px 4px 10px #BEBEBE50',
                  borderRadius: '12px',
                  fontFamily: 'Montserrat',
                  fontSize: '16px',
                  fontWeight: 600,
                  textTransform: 'none',
                  color: '#FFFFFF',
                  '&:hover': {
                    backgroundColor: '#0266A3'
                  }
                }}
              >
                Enregistrer
              </FormButton>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* Confirmation Dialogs */}
      {confirmDialogType === 'generic' && (
        <ConfirmationDialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmEdit}
          type="confirm"
          title="Confirmer les modifications"
          message="Êtes-vous sûr de vouloir enregistrer les modifications de cet utilisateur ?"
          confirmText="Enregistrer"
          cancelText="Annuler"
          loading={loading}
        />
      )}
      
      {confirmDialogType === 'deactivate' && (
        <ConfirmationDialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmEdit}
          type="deactivate"
          title="Désactiver l'utilisateur"
          messageComponent={
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0px',
                opacity: 1,
                color: '#333333'
              }}
            >
              Êtes-vous sûr de vouloir désactiver l'utilisateur{' '}
              <span style={{ color: '#027BC6' }}>{userName}</span>
              {' '}?
            </Typography>
          }
          confirmText="Désactiver"
          cancelText="Annuler"
          loading={loading}
        />
      )}
      
      {confirmDialogType === 'activate' && (
        <ConfirmationDialog
          open={showConfirmDialog}
          onClose={() => setShowConfirmDialog(false)}
          onConfirm={handleConfirmEdit}
          type="activate"
          title="Activer l'utilisateur"
          messageComponent={
            <Typography
              sx={{
                textAlign: 'center',
                fontFamily: 'Montserrat',
                fontWeight: 600,
                fontSize: '20px',
                lineHeight: '32px',
                letterSpacing: '0px',
                opacity: 1,
                color: '#333333'
              }}
            >
              Êtes-vous sûr de vouloir activer l'utilisateur{' '}
              <span style={{ color: '#027BC6' }}>{userName}</span>
              {' '}?
            </Typography>
          }
          confirmText="Activer"
          cancelText="Annuler"
          loading={loading}
        />
      )}
    </Dialog>
  );
};

export default UserEditModal;
